/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */
import { router } from '@/router';
import UserService from '@/services/user.service';
import {
  ValidationError, AuthError, TooManyAttemptsError, ForbiddenError,
} from '@/errors';
import * as routerHelper from '@/helpers/router.helper';
import { verifyEmail } from 'supertokens-web-js/recipe/emailverification';
import StorageService from '@/services/storage.service';
import TranslationHelper from '@/helpers/translation.helper';

const state = {
  authenticating: false,
  authErrorCode: 0,
  error: null,
  authError: '',
  validationError: null,
  rateLimitError: null,
  forbiddenError: null,
  resetPasswordSent: false,
  resetPasswordSuccess: false,
  changeEmailSent: false,
  currentRoute: '',
  registerSuccess: '',
  changeEmailSuccess: false,
  propertyGroup: null,
  propertyGroupLoading: false,
};

const getters = {
  authenticating: (state) => state.authenticating,
  authErrorCode: (state) => state.authErrorCode,
  authError: (state) => state.authError,
  validationError: (state) => state.validationError,
  error: (state) => state.error,
  rateLimitError: (state) => state.rateLimitError,
  forbiddenError: (state) => state.forbiddenError,
  resetPasswordSent: (state) => state.resetPasswordSent,
  resetPasswordSuccess: (state) => state.resetPasswordSuccess,
  changeEmailSent: (state) => state.changeEmailSent,
  currentRoute: (state) => state.currentRoute,
  registerSuccess: (state) => state.registerSuccess,
  changeEmailSuccess: (state) => state.changeEmailSuccess,
  propertyGroup: (state) => state.propertyGroup,
  propertyGroupLoading: (state) => state.propertyGroupLoading,
};

const mutations = {
  clearErrors(state) {
    state.authError = '';
    state.authErrorCode = 0;
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
  },
  error(state, data) {
    state.authenticating = false;
    state.error = data;
  },
  loginRequest(state) {
    state.authenticating = true;
    state.authError = '';
    state.authErrorCode = 0;
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
  },
  loginSuccess(state) {
    state.authenticating = false;
  },
  loginAuthError(state, { code, message }) {
    state.authenticating = false;
    state.authError = message;
    state.authErrorCode = code;
  },
  validationError(state, error) {
    state.authenticating = false;
    state.validationError = error;
  },
  rateLimitError(state, error) {
    state.authenticating = false;
    state.rateLimitError = error;
  },
  forbiddenError(state, error) {
    state.authenticating = false;
    state.forbiddenError = error;
  },
  sendPasswordReset(state) {
    state.authenticating = true;
    state.resetPasswordSent = false;
    state.rateLimitError = null;
    state.error = null;
  },
  sendEmailChange(state) {
    state.authenticating = true;
    state.changeEmailSent = false;
    state.rateLimitError = null;
  },
  resetPasswordSent(state) {
    state.authenticating = false;
    state.resetPasswordSent = true;
  },
  changeEmailSent(state) {
    state.authenticating = false;
    state.changeEmailSent = true;
  },
  resetPasswordRequest(state) {
    state.authenticating = true;
    state.validationError = null;
    state.rateLimitError = null;
  },
  changeEmailRequest(state) {
    state.authenticating = true;
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
    state.changeEmailSuccess = false;
  },
  changeEmailSuccess(state) {
    state.changeEmailSuccess = true;
  },
  resetPasswordComplete(state) {
    state.authenticating = false;
  },
  resetPasswordSuccess(state, data) {
    state.resetPasswordSuccess = data;
  },
  changeEmailComplete(state) {
    state.authenticating = false;
  },
  registerRequest(state) {
    state.authenticating = true;
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
  },
  registerSuccess(state) {
    state.authenticating = false;
    state.registerSuccess = true;
  },
  currentRoute(state, payload) {
    state.currentRoute = payload;
  },
  propertyGroup(state, payload) {
    state.propertyGroup = payload;
  },
  propertyGroupRequest(state) {
    state.propertyGroupLoading = true;
  },
  propertyGroupComplete(state) {
    state.propertyGroupLoading = false;
  },
  beforeCheckEmailAddressRequest(state) {
    state.authenticating = true;
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
  },
  afterCheckEmailAddressRequest(state) {
    state.authenticating = false;
  },
};
function handleError(commit, error) {
  if (error instanceof AuthError) {
    commit('loginAuthError', {
      code: error.errorCode,
      message: error.message,
    });
  }
  if (error instanceof ValidationError) {
    commit('validationError', error);
  } else if (error instanceof TooManyAttemptsError) {
    commit('rateLimitError', error);
  } else if (error instanceof ForbiddenError) {
    commit('forbiddenError', error);
  } else {
    let errorMessage = error.message;
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    commit('error', errorMessage);
  }
}

const actions = {

  async login({ commit, dispatch, rootGetters }, payload) {
    const { syncUser = null } = payload;
    if (!syncUser) {
      commit('loginRequest');
      commit('user/user', null, { root: true });
      commit('user/currentProperty', null, { root: true });
    }

    try {
      const user = syncUser != null ? syncUser : await UserService.login(payload);
      const userLandingProperty = user.landingPagePropertyId;
      if (userLandingProperty) {
        await dispatch('user/getCurrentProperty', { id: userLandingProperty, access: true }, { root: true });
        dispatch('user/getPropertyMainLanguage', {}, { root: true });
        dispatch('user/getUserPropertySetting', {}, { root: true });
      }
      await commit('loginSuccess', user);
      await commit('user/user', user, { root: true });
      const userLanguage = (rootGetters['user/user'])?.language;
      if (userLanguage) {
        const currentLanguage = StorageService.getLang();
        if (currentLanguage !== userLanguage) {
          await commit('appLoading', true, { root: true });
          await commit('user/setLang', userLanguage, { root: true });
          await TranslationHelper.updateTranslations(router, userLanguage, true);
          await commit('appLoading', false, { root: true });
        }
      }
      let redirectTo = router.history.current.query.redirect || { name: routerHelper.dashboardRouteName };
      if (!rootGetters['user/requiredFilled']) {
        redirectTo = { name: routerHelper.detailsRouteName };
      }
      await router.push(redirectTo);
    } catch (e) {
      handleError(commit, e);
    }
  },
  async loginFromIframe({ commit, dispatch, rootGetters }) {
    try {
      const user = await UserService.getUser();
      const userLandingProperty = user.landingPagePropertyId;
      if (userLandingProperty) {
        await dispatch('user/getCurrentProperty', { id: userLandingProperty, access: true }, { root: true });
        dispatch('user/getPropertyMainLanguage', {}, { root: true });
        dispatch('user/getUserPropertySetting', {}, { root: true });
      }
      await commit('loginSuccess', user);
      await commit('user/user', user, { root: true });
      const userLanguage = (rootGetters['user/user'])?.language;
      if (userLanguage) {
        const currentLanguage = StorageService.getLang();
        if (currentLanguage !== userLanguage) {
          await commit('appLoading', true, { root: true });
          await commit('user/setLang', userLanguage, { root: true });
          await TranslationHelper.updateTranslations(router, userLanguage, true);
          await commit('appLoading', false, { root: true });
        }
      }
      let redirectTo = router.history.current.query.redirect || { name: routerHelper.dashboardRouteName };
      if (!rootGetters['user/requiredFilled']) {
        redirectTo = { name: routerHelper.detailsRouteName };
      }
      await router.push(redirectTo);
    } catch (e) {
      handleError(commit, e);
    }
  },
  async directLogin({ commit, dispatch, rootGetters }, payload) {
    const { syncUser = null } = payload;
    if (!syncUser) {
      commit('loginRequest');
      commit('user/user', null, { root: true });
      commit('user/currentProperty', null, { root: true });
    }

    try {
      const user = syncUser != null ? syncUser : await UserService.directLogin(payload);
      const userLandingProperty = user.landingPagePropertyId;
      if (userLandingProperty) {
        await dispatch('user/getCurrentProperty', { id: userLandingProperty, access: true }, { root: true });
        dispatch('user/getUserPropertySetting', {}, { root: true });
      }
      await commit('loginSuccess', user);
      await commit('user/user', user, { root: true });
      const userLanguage = (rootGetters['user/user'])?.language;
      if (userLanguage) {
        const currentLanguage = StorageService.getLang();
        if (currentLanguage !== userLanguage) {
          await commit('appLoading', true, { root: true });
          await commit('user/setLang', userLanguage, { root: true });
          await TranslationHelper.updateTranslations(router, userLanguage, true);
          await commit('appLoading', false, { root: true });
        }
      }
      let redirectTo = { name: routerHelper.dashboardRouteName };
      if (!rootGetters['user/requiredFilled']) {
        redirectTo = { name: routerHelper.detailsRouteName };
      }
      await router.push(redirectTo);
    } catch (e) {
      handleError(commit, e);
    }
  },
  async directLoginFromVerifyEmail({ commit, dispatch, rootGetters }) {
    try {
      if (!await UserService.isEmailVerified()) {
        const verifyResponse = await verifyEmail();
        if (verifyResponse.status !== 'OK') throw new Error(verifyResponse.status);
      }

      const user = await UserService.getUser();
      const userLandingProperty = user.landingPagePropertyId;
      if (userLandingProperty) {
        await dispatch('user/getCurrentProperty', { id: userLandingProperty, access: true }, { root: true });
        dispatch('user/getUserPropertySetting', {}, { root: true });
      }
      await commit('loginSuccess', user);
      await commit('user/user', user, { root: true });

      const userLanguage = (rootGetters['user/user'])?.language;
      if (userLanguage) {
        const currentLanguage = StorageService.getLang();
        if (currentLanguage !== userLanguage) {
          await commit('appLoading', true, { root: true });
          await commit('user/setLang', userLanguage, { root: true });
          await TranslationHelper.updateTranslations(router, userLanguage, true);
          await commit('appLoading', false, { root: true });
        }
      }
      let redirectTo = router.history.current.query.redirect || '/';
      if (!rootGetters['user/requiredFilled']) {
        redirectTo = { name: routerHelper.detailsRouteName };
      }
      await router.push(redirectTo);
    } catch (e) {
      await commit('loginSuccess', false);
      handleError(commit, e);
    }
  },
  async logout({ commit, rootGetters }, { forced = false, stay = false, moveTo = null }) {
    const toRoute = moveTo || { name: routerHelper.loginRouteName };
    commit('appLoading', true, { root: true });
    commit('pageBreadcrumb', null, { root: true });
    if (stay) {
      const redirect = router.currentRoute.fullPath;
      if (redirect !== '/') {
        toRoute.query = { redirect };
      }
    }
    const otl = { ...rootGetters['user/userOTL'] };
    await UserService.logout(forced);
    if (otl.exit_url != null) {
      window.location = otl.exit_url;
      return;
    }
    commit('user/user', null, { root: true });
    commit('user/currentProperty', null, { root: true });
    commit('user/setProperty', null, { root: true });
    await router.push(toRoute);
  },

  async sendResetPasswordEmail({ commit }, { email }) {
    try {
      commit('sendPasswordReset');
      await UserService.sendResetPasswordEmail(email);
      commit('resetPasswordSent');
    } catch (e) {
      handleError(commit, e);
    }
  },

  async resetPassword({ commit }, { token, password, email }) {
    try {
      commit('resetPasswordRequest');
      commit('resetPasswordSuccess', false);
      await UserService.resetPassword(token, password, email);
      commit('resetPasswordSuccess', true);
      commit('resetPasswordComplete');
    } catch (e) {
      handleError(commit, e);
    }
  },

  async sendChangeEmail({ commit }) {
    try {
      commit('sendEmailChange');
      await UserService.sendChangeEmail();
      commit('changeEmailSent');
    } catch (e) {
      handleError(commit, e);
    }
  },
  async changeEmail({ commit }, { email, token, newEmail }) {
    try {
      commit('changeEmailRequest');
      const response = await UserService.changeEmail(email, token, newEmail);
      const message = await response.json();
      if (message.status === 'OK') {
        commit('changeEmailSuccess');
        commit('changeEmailComplete');
      } else {
        commit('error', message.message);
      }
    } catch (e) {
      handleError(commit, e);
    }
  },
  // eslint-disable-next-line camelcase
  async register({ commit, dispatch }, { email, password, tos_agreed }) {
    try {
      commit('registerRequest');
      await UserService.register(email, password, tos_agreed);
      commit('registerSuccess');
      dispatch('user/getUser', { redirect: false }, { root: true });
    } catch (e) {
      handleError(commit, e);
    }
  },
  async getPropertyGroup({ commit }) {
    try {
      commit('propertyGroupRequest');
      const data = await UserService.getPropertGroup();
      commit('propertyGroup', data);
      commit('propertyGroupComplete');
    } catch (e) {
      handleError(commit, e);
    }
  },
  async checkEmailAddress({ commit }, payload) {
    try {
      commit('beforeCheckEmailAddressRequest');
      const data = await UserService.checkEmailAddress(payload);
      commit('afterCheckEmailAddressRequest');
      return data;
    } catch (e) {
      commit('afterCheckEmailAddressRequest');
      handleError(commit, e);
    }
    return {
      status: false,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
